import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { getInvoicesPaginated } from "../api/client";
import { useUserSession } from "../context/UserContext";
import { IInvoice, IPaginatedResult } from "../types";
import { Invoice } from "../models";
import { queryKeys } from "../data/queryKeys";

const toInvoiceModel = (invoice: IInvoice): Invoice =>
  new Invoice({
    ...invoice,
    id: invoice.id || "",
    workspaceId: invoice.workspaceId || "",
    createdAt: invoice.createdAt || "",
    lastUpdated: invoice.lastUpdated || "",
    createdBy: invoice.createdBy || "",
    invoiceNumber: invoice.invoiceNumber || "",
    invoiceDate: invoice.invoiceDate || "",
    dueDate: invoice.dueDate || "",
    companyId: invoice.companyId || "",
    customerId: invoice.customerId || "",
  });

/**
 * Cursor-paginated invoices backed by the REST API, exposed as an infinite list.
 *
 * Same state model as {@link useInfiniteOrders}: the cursor is the `pageParam`,
 * pages accumulate in `data.pages`, and invalidation
 * (`useDataCache.invalidateInvoices()`) refetches every loaded page.
 *
 * Note: the invoices endpoint paginates but does not filter server-side, so any
 * client-side filtering/aggregation/export operates on the pages loaded so far.
 */
export const useInfiniteInvoices = (
  workspaceId: string,
  options: { limit?: number; enabled?: boolean } = {},
) => {
  const { limit = 20, enabled = true } = options;
  const { userSession, isAuthenticated } = useUserSession();

  const query = useInfiniteQuery<IPaginatedResult<IInvoice>, Error>(
    queryKeys.invoicesApi(workspaceId),
    async ({ pageParam }) => {
      if (!userSession?.token || !workspaceId || !isAuthenticated()) {
        throw new Error("Authentication or workspace data missing");
      }
      return await getInvoicesPaginated(
        userSession.token.accessToken,
        workspaceId,
        { limit, cursor: pageParam },
      );
    },
    {
      enabled:
        enabled && !!userSession?.token && !!workspaceId && isAuthenticated(),
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.nextCursor ?? undefined,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 300000,
    },
  );

  const invoices = useMemo(
    () => query.data?.pages.flatMap((page) => page.items.map(toInvoiceModel)) ?? [],
    [query.data],
  );

  return {
    ...query,
    invoices,
    hasNextPage: !!query.hasNextPage,
    isLoadingInitial: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
  };
};
